/**
 * Location Configuration
 *
 * Defines all campus locations with their properties, positions, and unlock requirements.
 * Maps between old tab-based navigation and new spatial navigation.
 */

import {
  CampusLocation,
  LocationConfig,
  CampusPosition,
  UnlockRequirement,
} from '../types/spatial';

/**
 * Campus layout positions for 3D scene
 */
export const CAMPUS_LAYOUT: Record<CampusLocation, CampusPosition> = {
  [CampusLocation.CAMPUS_OVERVIEW]: { x: 0, y: 50, z: 50 }, // Camera default position
  [CampusLocation.HEADQUARTERS]: { x: 0, y: 0, z: 0 },
  [CampusLocation.DESIGN_STUDIO]: { x: -20, y: 0, z: 12 },
  [CampusLocation.RD_LAB]: { x: -20, y: 0, z: -12 },
  [CampusLocation.MANUFACTURING]: { x: 20, y: 0, z: 12 },
  [CampusLocation.SALES_OFFICE]: { x: 20, y: 0, z: -12 },
  [CampusLocation.EXECUTIVE_OFFICE]: { x: 0, y: 0, z: -25 },
  [CampusLocation.COMPANY_ARCHIVES]: { x: 0, y: 0, z: 20 },
};

/**
 * Building colors for 3D models
 *
 * Semantic color system -- colors map to functional categories:
 *   Core/Command:  Deep blues/slate (HQ, Executive) -- authority and control
 *   Creative:      Warm copper/amber (Design Studio) -- innovation and craft
 *   Technical:     Cool teal/cyan (R&D Lab) -- precision and discovery
 *   Production:    Warm steel/terracotta (Factory) -- industry and output
 *   Commercial:    Rich gold/brass (Sales) -- commerce and growth
 *   Archival:      Muted sage (Archives) -- history and knowledge
 */
export const BUILDING_COLORS = {
  HEADQUARTERS: '#3a6b8c',     // Steel blue -- command center
  DESIGN_STUDIO: '#b87333',    // Copper -- creative warmth
  RD_LAB: '#2a8a7a',           // Teal -- scientific precision
  MANUFACTURING: '#8b5c3a',    // Warm bronze -- industrial craft
  SALES_OFFICE: '#b8963e',     // Brass gold -- commercial ambition
  EXECUTIVE_OFFICE: '#2c3e50', // Dark slate -- executive authority
  COMPANY_ARCHIVES: '#5a7a6a', // Sage green -- knowledge & history
  LOCKED: '#6b7280',           // Neutral gray -- inaccessible
};

/**
 * Unlock requirements for each location
 */
const UNLOCK_REQUIREMENTS: Partial<Record<CampusLocation, UnlockRequirement[]>> = {
  [CampusLocation.MANUFACTURING]: [
    {
      type: 'productsInDevelopment',
      value: 1,
      description: 'Start a product in the Design Studio',
    },
  ],
  [CampusLocation.RD_LAB]: [
    {
      type: 'reputation',
      value: 20,
      description: 'Grow company reputation to 20',
    },
  ],
  [CampusLocation.SALES_OFFICE]: [
    {
      type: 'productsReleased',
      value: 1,
      description: 'Release a product from the Design Studio',
    },
  ],
  [CampusLocation.EXECUTIVE_OFFICE]: [
    {
      type: 'cash',
      value: 100000,
      description: 'Accumulate $100,000 in cash',
    },
  ],
  [CampusLocation.COMPANY_ARCHIVES]: [
    {
      type: 'reputation',
      value: 10,
      description: 'Build company reputation to 10',
    },
  ],
};

/**
 * Adjacent locations for spatial navigation hints
 */
const ADJACENT_LOCATIONS: Record<CampusLocation, CampusLocation[]> = {
  [CampusLocation.CAMPUS_OVERVIEW]: [
    CampusLocation.HEADQUARTERS,
    CampusLocation.DESIGN_STUDIO,
    CampusLocation.MANUFACTURING,
    CampusLocation.RD_LAB,
    CampusLocation.SALES_OFFICE,
    CampusLocation.EXECUTIVE_OFFICE,
    CampusLocation.COMPANY_ARCHIVES,
  ],
  [CampusLocation.HEADQUARTERS]: [
    CampusLocation.DESIGN_STUDIO,
    CampusLocation.MANUFACTURING,
    CampusLocation.COMPANY_ARCHIVES,
  ],
  [CampusLocation.DESIGN_STUDIO]: [
    CampusLocation.HEADQUARTERS,
    CampusLocation.RD_LAB,
  ],
  [CampusLocation.RD_LAB]: [
    CampusLocation.DESIGN_STUDIO,
    CampusLocation.EXECUTIVE_OFFICE,
  ],
  [CampusLocation.MANUFACTURING]: [
    CampusLocation.HEADQUARTERS,
    CampusLocation.SALES_OFFICE,
  ],
  [CampusLocation.SALES_OFFICE]: [
    CampusLocation.MANUFACTURING,
    CampusLocation.EXECUTIVE_OFFICE,
  ],
  [CampusLocation.EXECUTIVE_OFFICE]: [
    CampusLocation.RD_LAB,
    CampusLocation.SALES_OFFICE,
  ],
  [CampusLocation.COMPANY_ARCHIVES]: [
    CampusLocation.HEADQUARTERS,
  ],
};

/**
 * Full location configurations
 */
export const LOCATION_CONFIGS: Record<CampusLocation, LocationConfig> = {
  [CampusLocation.CAMPUS_OVERVIEW]: {
    id: CampusLocation.CAMPUS_OVERVIEW,
    name: 'Campus Overview',
    description: 'Bird\'s eye view of your entire corporate campus',
    buildingName: 'Campus',
    position: CAMPUS_LAYOUT[CampusLocation.CAMPUS_OVERVIEW],
    rotation: 0,
    color: '#ffffff',
    lockedColor: BUILDING_COLORS.LOCKED,
    adjacentLocations: ADJACENT_LOCATIONS[CampusLocation.CAMPUS_OVERVIEW],
  },
  [CampusLocation.HEADQUARTERS]: {
    id: CampusLocation.HEADQUARTERS,
    name: 'Headquarters',
    description: 'Central hub for company operations and overview',
    buildingName: 'Main Building',
    position: CAMPUS_LAYOUT[CampusLocation.HEADQUARTERS],
    rotation: 0,
    color: BUILDING_COLORS.HEADQUARTERS,
    lockedColor: BUILDING_COLORS.LOCKED,
    adjacentLocations: ADJACENT_LOCATIONS[CampusLocation.HEADQUARTERS],
  },
  [CampusLocation.DESIGN_STUDIO]: {
    id: CampusLocation.DESIGN_STUDIO,
    name: 'Design Studio',
    description: 'Where new products are conceived and designed',
    buildingName: 'Design Studio',
    position: CAMPUS_LAYOUT[CampusLocation.DESIGN_STUDIO],
    rotation: Math.PI / 6,
    color: BUILDING_COLORS.DESIGN_STUDIO,
    lockedColor: BUILDING_COLORS.LOCKED,
    adjacentLocations: ADJACENT_LOCATIONS[CampusLocation.DESIGN_STUDIO],
  },
  [CampusLocation.RD_LAB]: {
    id: CampusLocation.RD_LAB,
    name: 'R&D Laboratory',
    description: 'Research and development center for new technologies',
    buildingName: 'R&D Lab',
    position: CAMPUS_LAYOUT[CampusLocation.RD_LAB],
    rotation: -Math.PI / 6,
    color: BUILDING_COLORS.RD_LAB,
    lockedColor: BUILDING_COLORS.LOCKED,
    adjacentLocations: ADJACENT_LOCATIONS[CampusLocation.RD_LAB],
    unlockRequirements: UNLOCK_REQUIREMENTS[CampusLocation.RD_LAB],
  },
  [CampusLocation.MANUFACTURING]: {
    id: CampusLocation.MANUFACTURING,
    name: 'Manufacturing',
    description: 'Factory floor for producing your devices',
    buildingName: 'Factory',
    position: CAMPUS_LAYOUT[CampusLocation.MANUFACTURING],
    rotation: -Math.PI / 6,
    color: BUILDING_COLORS.MANUFACTURING,
    lockedColor: BUILDING_COLORS.LOCKED,
    adjacentLocations: ADJACENT_LOCATIONS[CampusLocation.MANUFACTURING],
    unlockRequirements: UNLOCK_REQUIREMENTS[CampusLocation.MANUFACTURING],
  },
  [CampusLocation.SALES_OFFICE]: {
    id: CampusLocation.SALES_OFFICE,
    name: 'Sales Office',
    description: 'Marketing and sales operations center',
    buildingName: 'Sales Office',
    position: CAMPUS_LAYOUT[CampusLocation.SALES_OFFICE],
    rotation: Math.PI / 6,
    color: BUILDING_COLORS.SALES_OFFICE,
    lockedColor: BUILDING_COLORS.LOCKED,
    adjacentLocations: ADJACENT_LOCATIONS[CampusLocation.SALES_OFFICE],
    unlockRequirements: UNLOCK_REQUIREMENTS[CampusLocation.SALES_OFFICE],
  },
  [CampusLocation.EXECUTIVE_OFFICE]: {
    id: CampusLocation.EXECUTIVE_OFFICE,
    name: 'Executive Office',
    description: 'Financial management and executive decisions',
    buildingName: 'Executive Tower',
    position: CAMPUS_LAYOUT[CampusLocation.EXECUTIVE_OFFICE],
    rotation: 0,
    color: BUILDING_COLORS.EXECUTIVE_OFFICE,
    lockedColor: BUILDING_COLORS.LOCKED,
    adjacentLocations: ADJACENT_LOCATIONS[CampusLocation.EXECUTIVE_OFFICE],
    unlockRequirements: UNLOCK_REQUIREMENTS[CampusLocation.EXECUTIVE_OFFICE],
  },
  [CampusLocation.COMPANY_ARCHIVES]: {
    id: CampusLocation.COMPANY_ARCHIVES,
    name: 'Company Archives',
    description: 'Historical records and event archives',
    buildingName: 'Company Archives',
    position: CAMPUS_LAYOUT[CampusLocation.COMPANY_ARCHIVES],
    rotation: 0,
    color: BUILDING_COLORS.COMPANY_ARCHIVES,
    lockedColor: BUILDING_COLORS.LOCKED,
    adjacentLocations: ADJACENT_LOCATIONS[CampusLocation.COMPANY_ARCHIVES],
    unlockRequirements: UNLOCK_REQUIREMENTS[CampusLocation.COMPANY_ARCHIVES],
  },
};

/**
 * Get location config by ID
 */
export const getLocationConfig = (location: CampusLocation): LocationConfig => {
  return LOCATION_CONFIGS[location];
};

/**
 * Get all location configs as an array (excluding campus overview)
 */
export const getAllBuildingConfigs = (): LocationConfig[] => {
  return Object.values(LOCATION_CONFIGS).filter(
    (config) => config.id !== CampusLocation.CAMPUS_OVERVIEW
  );
};

/**
 * Get adjacent locations for a given location
 */
export const getAdjacentLocations = (location: CampusLocation): CampusLocation[] => {
  return ADJACENT_LOCATIONS[location] ?? [];
};

/**
 * Get unlock requirements for a location
 */
export const getUnlockRequirements = (
  location: CampusLocation
): UnlockRequirement[] => {
  return UNLOCK_REQUIREMENTS[location] ?? [];
};

/**
 * Check if a location has unlock requirements
 */
export const hasUnlockRequirements = (location: CampusLocation): boolean => {
  const requirements = UNLOCK_REQUIREMENTS[location];
  return requirements !== undefined && requirements.length > 0;
};

/**
 * Mapping from dashboard tabs to campus locations
 */
export const TAB_TO_LOCATION: Record<string, CampusLocation> = {
  Headquarters: CampusLocation.HEADQUARTERS,
  Products: CampusLocation.DESIGN_STUDIO,
  Operations: CampusLocation.MANUFACTURING,
  Research: CampusLocation.RD_LAB,
  Marketing: CampusLocation.SALES_OFFICE,
  Finance: CampusLocation.EXECUTIVE_OFFICE,
  Archives: CampusLocation.COMPANY_ARCHIVES,
  Company: CampusLocation.HEADQUARTERS,
};

/**
 * Mapping from campus locations to dashboard tabs
 */
export const LOCATION_TO_TAB: Record<CampusLocation, string> = {
  [CampusLocation.CAMPUS_OVERVIEW]: 'Headquarters',
  [CampusLocation.HEADQUARTERS]: 'Headquarters',
  [CampusLocation.DESIGN_STUDIO]: 'Products',
  [CampusLocation.MANUFACTURING]: 'Operations',
  [CampusLocation.RD_LAB]: 'Research',
  [CampusLocation.SALES_OFFICE]: 'Marketing',
  [CampusLocation.EXECUTIVE_OFFICE]: 'Finance',
  [CampusLocation.COMPANY_ARCHIVES]: 'Archives',
};
